import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getAnalytics, runPaymentReminders, getSchedulerLogs, getPaymentReminders,
  sendWhatsAppMessage, getWhatsAppLogs, getWhatsAppStats,
  getAdminReferralStats, getAdminRiskOverview
} from '../services/api';
import { EdFiLogo } from '../components/EdFiLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area
} from 'recharts';
import {
  TrendingUp, DollarSign, Users, BookOpen, LogOut, ArrowLeft,
  Bell, MessageSquare, Clock, Send, Loader2, CheckCircle,
  AlertTriangle, BarChart3, PieChart as PieChartIcon, Activity,
  Gift, Shield
} from 'lucide-react';

const COLORS = ['#0033A0', '#E91E8C', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#84CC16'];

const STATUS_LABELS = {
  draft: 'Borrador', submitted: 'Enviada', under_review: 'En revision',
  approved: 'Aprobada', rejected: 'Rechazada', disbursed: 'Desembolsado',
  documents_required: 'Docs pendientes'
};

const AnalyticsDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Scheduler
  const [schedulerLogs, setSchedulerLogs] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [runningScheduler, setRunningScheduler] = useState(false);
  
  // WhatsApp
  const [waStats, setWaStats] = useState(null);
  const [waLogs, setWaLogs] = useState([]);
  const [waForm, setWaForm] = useState({ phone: '', template: 'general', message: '' });
  const [sendingWa, setSendingWa] = useState(false);
  
  // Referrals & Risk
  const [referralStats, setReferralStats] = useState(null);
  const [riskOverview, setRiskOverview] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchAnalytics();
  }, [user, navigate]);

  const fetchAnalytics = async () => {
    try {
      const res = await getAnalytics();
      setAnalytics(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchSchedulerData = async () => {
    try {
      const [logsRes, remindersRes] = await Promise.all([
        getSchedulerLogs(), getPaymentReminders()
      ]);
      setSchedulerLogs(logsRes.data || []);
      setReminders(remindersRes.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchWhatsAppData = async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        getWhatsAppStats(), getWhatsAppLogs()
      ]);
      setWaStats(statsRes.data);
      setWaLogs(logsRes.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchReferralData = async () => {
    try {
      const res = await getAdminReferralStats();
      setReferralStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchRiskData = async () => {
    try {
      const res = await getAdminRiskOverview();
      setRiskOverview(res.data);
    } catch (e) { console.error(e); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'scheduler') fetchSchedulerData();
    if (tab === 'whatsapp') fetchWhatsAppData();
    if (tab === 'referrals') fetchReferralData();
    if (tab === 'risk') fetchRiskData();
  };

  const handleRunScheduler = async () => {
    setRunningScheduler(true);
    try {
      const res = await runPaymentReminders();
      alert(`Recordatorios ejecutados: ${res.data.reminders_sent} enviados, ${res.data.overdue_found} vencidos`);
      fetchSchedulerData();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    setRunningScheduler(false);
  };

  const handleSendWhatsApp = async () => {
    setSendingWa(true);
    try {
      const res = await sendWhatsAppMessage(waForm);
      alert(`WhatsApp ${res.data.status}: ${res.data.message?.slice(0, 60)}...`);
      setWaForm({ phone: '', template: 'general', message: '' });
      fetchWhatsAppData();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    setSendingWa(false);
  };

  const formatPrice = (p) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(p || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-brand-blue text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <EdFiLogo size="sm" linkTo="/" className="brightness-0 invert" />
            <Badge className="bg-white/20 text-white">Analitica</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" data-testid="back-admin-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Admin
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className="text-white hover:bg-white/10">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="analytics-title">Analitica y Operaciones</h1>
        <p className="text-slate-600 mb-8">Monitoreo en tiempo real del negocio Ed.Fi</p>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-analytics-overview"><BarChart3 className="w-4 h-4 mr-1" /> Dashboard</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-analytics-revenue"><DollarSign className="w-4 h-4 mr-1" /> Ingresos</TabsTrigger>
            <TabsTrigger value="scheduler" data-testid="tab-scheduler"><Bell className="w-4 h-4 mr-1" /> Recordatorios</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp"><MessageSquare className="w-4 h-4 mr-1" /> WhatsApp</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals"><Gift className="w-4 h-4 mr-1" /> Referidos</TabsTrigger>
            <TabsTrigger value="risk" data-testid="tab-risk"><Shield className="w-4 h-4 mr-1" /> Riesgo</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {analytics && (
              <div className="space-y-6">
                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Prestamos Activos</p><p className="text-3xl font-bold text-brand-blue" data-testid="kpi-active-loans">{analytics.loans.active_loans}</p></CardContent></Card>
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Total Desembolsado</p><p className="text-2xl font-bold text-green-600">{formatPrice(analytics.loans.total_disbursed)}</p></CardContent></Card>
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Margen Ed.Fi (20%)</p><p className="text-2xl font-bold text-brand-magenta">{formatPrice(analytics.disbursements.total_edfi_margin)}</p></CardContent></Card>
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Usuarios</p><p className="text-2xl font-bold">{analytics.users_by_role.reduce((s, u) => s + u.count, 0)}</p></CardContent></Card>
                </div>

                {/* Charts Row 1 */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Applications by Status */}
                  <Card>
                    <CardHeader><CardTitle className="flex items-center gap-2"><PieChartIcon className="w-5 h-5 text-brand-blue" /> Solicitudes por Estado</CardTitle></CardHeader>
                    <CardContent>
                      {analytics.applications.by_status.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <Pie data={analytics.applications.by_status.map(s => ({ ...s, name: STATUS_LABELS[s.status] || s.status }))} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="count" nameKey="name" label={({ name, count }) => `${name}: ${count}`}>
                              {analytics.applications.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : <p className="text-center text-slate-500 py-12">Sin datos de solicitudes</p>}
                    </CardContent>
                  </Card>

                  {/* Programs by Category */}
                  <Card>
                    <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-brand-blue" /> Programas por Categoria</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={analytics.programs.by_category} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis type="category" dataKey="category" width={120} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v, n) => [n === 'avg_price' ? formatPrice(v) : v, n === 'avg_price' ? 'Precio Promedio' : 'Programas']} />
                          <Bar dataKey="count" fill="#0033A0" radius={[0, 4, 4, 0]} name="Programas" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row 2 */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Credit Score Distribution */}
                  <Card>
                    <CardHeader><CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5 text-brand-blue" /> Distribucion de Scores Crediticios</CardTitle></CardHeader>
                    <CardContent>
                      {analytics.credit_scores.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={analytics.credit_scores}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="range" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={60} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="count" fill="#E91E8C" radius={[4, 4, 0, 0]} name="Solicitudes" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <p className="text-center text-slate-500 py-12">Sin datos de scores (requiere solicitudes con Buro)</p>}
                    </CardContent>
                  </Card>

                  {/* Programs by University */}
                  <Card>
                    <CardHeader><CardTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-brand-blue" /> Programas por Universidad</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={analytics.programs.by_university} cx="50%" cy="50%" outerRadius={100} dataKey="count" nameKey="name" label={({ name, count }) => `${name}: ${count}`}>
                            {analytics.programs.by_university.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v, n, p) => [n === 'total_value' ? formatPrice(v) : v, n === 'total_value' ? 'Valor Total' : 'Programas']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Users by Role */}
                <Card>
                  <CardHeader><CardTitle>Usuarios por Rol</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {analytics.users_by_role.map((u, i) => (
                        <div key={u.role} className="text-center p-4 rounded-xl" style={{ backgroundColor: COLORS[i % COLORS.length] + '15' }}>
                          <p className="text-3xl font-bold" style={{ color: COLORS[i % COLORS.length] }}>{u.count}</p>
                          <p className="text-sm text-slate-600 capitalize">{u.role === 'student' ? 'Estudiantes' : u.role === 'admin' ? 'Admins' : 'Universidades'}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            {analytics && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-4 gap-4">
                  <Card className="bg-brand-blue text-white"><CardContent className="pt-6"><p className="text-white/70 text-sm">Total Desembolsado</p><p className="text-3xl font-bold">{formatPrice(analytics.loans.total_disbursed)}</p></CardContent></Card>
                  <Card className="bg-brand-magenta text-white"><CardContent className="pt-6"><p className="text-white/70 text-sm">Margen Ed.Fi (20%)</p><p className="text-3xl font-bold">{formatPrice(analytics.disbursements.total_edfi_margin)}</p></CardContent></Card>
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Pagado a Universidades</p><p className="text-2xl font-bold text-green-600">{formatPrice(analytics.disbursements.paid_payouts)}</p></CardContent></Card>
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Pendiente de Pago</p><p className="text-2xl font-bold text-amber-600">{formatPrice(analytics.disbursements.pending_payouts)}</p></CardContent></Card>
                </div>

                {/* Revenue breakdown */}
                <Card>
                  <CardHeader><CardTitle>Modelo de Negocio Ed.Fi - Flujo de Ingresos</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-6 py-6">
                      <div className="text-center p-6 bg-blue-50 rounded-xl">
                        <p className="text-sm text-slate-600 mb-2">Alumno Paga (100% + interes)</p>
                        <p className="text-3xl font-bold text-brand-blue">{formatPrice(analytics.loans.total_disbursed)}</p>
                        <p className="text-xs text-slate-500 mt-1">Cobro mensual: {formatPrice(analytics.loans.total_monthly_revenue)}</p>
                      </div>
                      <div className="text-center p-6 bg-green-50 rounded-xl">
                        <p className="text-sm text-slate-600 mb-2">Ed.Fi Paga a Universidad (80%)</p>
                        <p className="text-3xl font-bold text-green-600">{formatPrice(analytics.disbursements.total_uni_payouts)}</p>
                        <p className="text-xs text-slate-500 mt-1">{analytics.loans.total_loans} operaciones</p>
                      </div>
                      <div className="text-center p-6 bg-pink-50 rounded-xl">
                        <p className="text-sm text-slate-600 mb-2">Margen Ed.Fi (20% descuento)</p>
                        <p className="text-3xl font-bold text-brand-magenta">{formatPrice(analytics.disbursements.total_edfi_margin)}</p>
                        <p className="text-xs text-slate-500 mt-1">+ intereses cobrados al alumno</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Applications trend */}
                <Card>
                  <CardHeader><CardTitle>Solicitudes por Mes</CardTitle></CardHeader>
                  <CardContent>
                    {analytics.applications.by_month.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={analytics.applications.by_month}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip formatter={(v, n) => [n === 'approved' || n === 'requested' ? formatPrice(v) : v, n === 'count' ? 'Solicitudes' : n === 'requested' ? 'Solicitado' : 'Aprobado']} />
                          <Area type="monotone" dataKey="count" stroke="#0033A0" fill="#0033A0" fillOpacity={0.1} name="Solicitudes" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : <p className="text-center text-slate-500 py-12">Sin datos mensuales aun</p>}
                  </CardContent>
                </Card>

                {/* Price by Category */}
                <Card>
                  <CardHeader><CardTitle>Precio Promedio por Categoria</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analytics.programs.by_category}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={60} />
                        <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [formatPrice(v), 'Precio Promedio']} />
                        <Bar dataKey="avg_price" fill="#E91E8C" radius={[4, 4, 0, 0]} name="Precio Promedio" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Scheduler Tab */}
          <TabsContent value="scheduler">
            <div className="space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-brand-blue" /> Scheduler de Recordatorios</CardTitle>
                    <p className="text-sm text-slate-600 mt-1">Cron automatico diario a las 9:00 UTC (3:00 AM CDMX). Tambien puedes ejecutar manualmente.</p>
                  </div>
                  <Button onClick={handleRunScheduler} disabled={runningScheduler} className="bg-brand-blue" data-testid="run-scheduler-btn">
                    {runningScheduler ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                    Ejecutar Ahora
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium mb-2">Configuracion:</h4>
                    <ul className="space-y-1 text-sm text-slate-600">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Recordatorios: 7, 3 y 1 dia(s) antes del vencimiento</li>
                      <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Vencidos: alertas a 1, 3, 7, 15 y 30 dias de retraso</li>
                      <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-brand-blue" /> Canales: Notificacion in-app + WhatsApp (si telefono disponible)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Scheduler Logs */}
              <Card>
                <CardHeader><CardTitle>Historial de Ejecuciones</CardTitle></CardHeader>
                <CardContent>
                  {schedulerLogs.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No hay ejecuciones registradas. Haz clic en "Ejecutar Ahora" para probar.</p>
                  ) : (
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Fecha</TableHead><TableHead>Prestamos Revisados</TableHead>
                        <TableHead>Recordatorios Enviados</TableHead><TableHead>Vencidos Encontrados</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {schedulerLogs.map((log, i) => (
                          <TableRow key={i}>
                            <TableCell>{formatDate(log.ran_at)}</TableCell>
                            <TableCell>{log.active_loans_checked || log.loans_checked}</TableCell>
                            <TableCell className="text-green-600 font-medium">{log.reminders_sent}</TableCell>
                            <TableCell className="text-amber-600 font-medium">{log.overdue_found}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Recent Reminders */}
              {reminders.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>Recordatorios Recientes</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Prestamo</TableHead><TableHead>Tipo</TableHead>
                        <TableHead>Monto</TableHead><TableHead>Canales</TableHead><TableHead>Fecha</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {reminders.slice(0, 20).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono text-xs">{r.loan_id?.slice(0, 15)}</TableCell>
                            <TableCell>
                              <Badge className={r.type === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                                {r.type === 'overdue' ? `Vencido ${r.days_overdue}d` : `En ${r.days_until_due}d`}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatPrice(r.amount)}</TableCell>
                            <TableCell>{r.channels?.join(', ')}</TableCell>
                            <TableCell>{formatDate(r.sent_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp">
            <div className="space-y-6">
              {/* WhatsApp Status */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-green-500" /> WhatsApp Business</CardTitle>
                    <p className="text-sm text-slate-600 mt-1">
                      {waStats?.enabled
                        ? 'Conectado con Twilio WhatsApp Business API'
                        : 'Modo SIMULADO - Configura TWILIO_ACCOUNT_SID y TWILIO_AUTH_TOKEN en .env para activar'}
                    </p>
                  </div>
                  <Badge className={waStats?.enabled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {waStats?.enabled ? 'Activo' : 'Simulado'}
                  </Badge>
                </CardHeader>
                <CardContent>
                  {waStats && (
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{waStats.total_messages || 0}</p>
                        <p className="text-sm text-slate-600">Total Mensajes</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-brand-blue">{waStats.by_status?.simulated || waStats.by_status?.sent || 0}</p>
                        <p className="text-sm text-slate-600">Enviados</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{waStats.by_status?.failed || 0}</p>
                        <p className="text-sm text-slate-600">Fallidos</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Send Message */}
              <Card>
                <CardHeader><CardTitle>Enviar Mensaje WhatsApp</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium">Telefono (10 digitos Mexico)</label>
                      <Input value={waForm.phone} onChange={e => setWaForm({ ...waForm, phone: e.target.value })} placeholder="5512345678" data-testid="wa-phone-input" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Plantilla</label>
                      <Select value={waForm.template} onValueChange={v => setWaForm({ ...waForm, template: v })}>
                        <SelectTrigger data-testid="wa-template-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="welcome">Bienvenida</SelectItem>
                          <SelectItem value="payment_reminder">Recordatorio de Pago</SelectItem>
                          <SelectItem value="application_approved">Solicitud Aprobada</SelectItem>
                          <SelectItem value="contract_signed">Contrato Firmado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-sm font-medium">Mensaje (plantilla 'general')</label>
                    <textarea className="flex w-full rounded-md border px-3 py-2 text-sm" rows={3} value={waForm.message} onChange={e => setWaForm({ ...waForm, message: e.target.value })} placeholder="Escribe tu mensaje..." data-testid="wa-message-input" />
                  </div>
                  <Button onClick={handleSendWhatsApp} disabled={sendingWa || !waForm.phone} className="bg-green-600 hover:bg-green-700" data-testid="wa-send-btn">
                    {sendingWa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Enviar WhatsApp
                  </Button>
                </CardContent>
              </Card>

              {/* Message Logs */}
              {waLogs.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>Historial de Mensajes</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Destinatario</TableHead><TableHead>Plantilla</TableHead>
                        <TableHead>Mensaje</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {waLogs.slice(0, 20).map((log, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono text-xs">{log.to}</TableCell>
                            <TableCell><Badge variant="outline">{log.template}</Badge></TableCell>
                            <TableCell className="max-w-[200px] truncate text-sm">{log.message}</TableCell>
                            <TableCell>
                              <Badge className={log.status === 'sent' ? 'bg-green-100 text-green-700' : log.status === 'simulated' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}>
                                {log.status === 'simulated' ? 'Simulado' : log.status === 'sent' ? 'Enviado' : 'Fallido'}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(log.sent_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Templates */}
              <Card>
                <CardHeader><CardTitle>Plantillas Disponibles</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { id: 'payment_reminder', name: 'Recordatorio de Pago', preview: 'Tu pago de {amount} por {program} vence en {days} dia(s).' },
                      { id: 'payment_overdue', name: 'Pago Vencido', preview: 'Tu pago de {amount} por {program} esta vencido por {days_overdue} dia(s).' },
                      { id: 'application_approved', name: 'Solicitud Aprobada', preview: 'Tu solicitud de credito para {program} por {amount} ha sido aprobada.' },
                      { id: 'contract_signed', name: 'Contrato Firmado', preview: 'Tu contrato para {program} ha sido firmado. Primer pago en 30 dias.' },
                      { id: 'welcome', name: 'Bienvenida', preview: 'Bienvenido a ed.fi, {name}! Study Now, Pay Later.' },
                    ].map(t => (
                      <div key={t.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{t.name}</p>
                          <p className="text-xs text-slate-500">{t.preview}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{t.id}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <div className="space-y-6">
              {referralStats && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Codigos Generados</p><p className="text-3xl font-bold text-brand-blue" data-testid="ref-codes-count">{referralStats.total_codes_generated}</p></CardContent></Card>
                    <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Total Referidos</p><p className="text-3xl font-bold text-purple-600">{referralStats.total_referrals}</p></CardContent></Card>
                    <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Inscritos</p><p className="text-3xl font-bold text-green-600">{referralStats.total_enrolled}</p><p className="text-xs text-slate-500">Conversion: {referralStats.conversion_rate}%</p></CardContent></Card>
                    <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Total Descuentos</p><p className="text-2xl font-bold text-brand-magenta">{formatPrice(referralStats.total_referrer_payouts + referralStats.total_referred_discounts)}</p></CardContent></Card>
                  </div>
                  
                  {referralStats.total_referrals > 0 && (
                    <Card>
                      <CardHeader><CardTitle>Funnel de Referidos</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={[
                            { stage: 'Codigos', value: referralStats.total_codes_generated },
                            { stage: 'Invitados', value: referralStats.total_referrals },
                            { stage: 'Inscritos', value: referralStats.total_enrolled },
                          ]}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="stage" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#E91E8C" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {referralStats.top_referrers.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle>Top Referidores</CardTitle></CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead>Usuario</TableHead><TableHead>Codigo</TableHead>
                            <TableHead className="text-right">Referidos</TableHead>
                            <TableHead className="text-right">Inscritos</TableHead>
                            <TableHead className="text-right">Ganado</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {referralStats.top_referrers.map(r => (
                              <TableRow key={r.referral_id}>
                                <TableCell className="font-medium">{r.user_name}</TableCell>
                                <TableCell className="font-mono">{r.code}</TableCell>
                                <TableCell className="text-right">{r.total_referred}</TableCell>
                                <TableCell className="text-right text-green-600">{r.total_enrolled}</TableCell>
                                <TableCell className="text-right font-bold">{formatPrice(r.total_earnings)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
              {!referralStats && <p className="text-center text-slate-500 py-12">Cargando datos de referidos...</p>}
            </div>
          </TabsContent>

          {/* Risk Score Tab */}
          <TabsContent value="risk">
            <div className="space-y-6">
              {riskOverview && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Total Evaluados</p><p className="text-3xl font-bold text-brand-blue" data-testid="risk-total">{riskOverview.total_scored}</p></CardContent></Card>
                    <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Score Promedio</p><p className="text-3xl font-bold">{riskOverview.average_score}</p></CardContent></Card>
                    <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Score Minimo</p><p className="text-3xl font-bold text-red-500">{riskOverview.min_score}</p></CardContent></Card>
                    <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Score Maximo</p><p className="text-3xl font-bold text-green-600">{riskOverview.max_score}</p></CardContent></Card>
                  </div>

                  {/* Risk Tiers */}
                  <Card>
                    <CardHeader><CardTitle>Niveles de Riesgo y Tasas</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                        {Object.entries(riskOverview.risk_tiers || {}).map(([key, tier]) => {
                          const tierData = riskOverview.by_tier.find(t => t.tier === key);
                          return (
                            <div key={key} className="text-center p-4 rounded-xl border-2" style={{ borderColor: tier.color + '40', backgroundColor: tier.color + '08' }}>
                              <div className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: tier.color }}>
                                {tierData?.count || 0}
                              </div>
                              <p className="font-bold text-sm" style={{ color: tier.color }}>{tier.label}</p>
                              <p className="text-xs text-slate-500">{tier.min_score}-{tier.max_score} pts</p>
                              <p className="text-xs font-semibold mt-1">{(tier.rate * 100).toFixed(1)}% mensual</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {riskOverview.by_tier.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle>Distribucion por Tier de Riesgo</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <Pie data={riskOverview.by_tier.map(t => ({ name: t.label, value: t.count }))} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`}>
                              {riskOverview.by_tier.map((t, i) => {
                                const tier = riskOverview.risk_tiers?.[t.tier];
                                return <Cell key={i} fill={tier?.color || COLORS[i % COLORS.length]} />;
                              })}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Recent Scores */}
                  {riskOverview.recent_scores.length > 0 && (
                    <Card>
                      <CardHeader><CardTitle>Scores Recientes</CardTitle></CardHeader>
                      <CardContent>
                        <div className="table-scroll">
                          <Table>
                            <TableHeader><TableRow>
                              <TableHead>Usuario</TableHead><TableHead>Score</TableHead>
                              <TableHead>Tier</TableHead><TableHead>Tasa</TableHead>
                              <TableHead className="hidden sm:table-cell">Buro</TableHead>
                              <TableHead className="hidden sm:table-cell">IMSS</TableHead>
                              <TableHead className="hidden sm:table-cell">Pagos</TableHead>
                              <TableHead className="hidden sm:table-cell">DTI</TableHead>
                              <TableHead>Fecha</TableHead>
                            </TableRow></TableHeader>
                            <TableBody>
                              {riskOverview.recent_scores.map(s => (
                                <TableRow key={s.user_id}>
                                  <TableCell className="font-mono text-xs">{s.user_id?.slice(0, 12)}</TableCell>
                                  <TableCell>
                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white font-bold text-sm" style={{ backgroundColor: s.tier_color }}>
                                      {Math.round(s.composite_score)}
                                    </span>
                                  </TableCell>
                                  <TableCell><Badge style={{ backgroundColor: s.tier_color + '20', color: s.tier_color }}>{s.tier_label}</Badge></TableCell>
                                  <TableCell className="font-semibold">{s.adjusted_rate_annual}%</TableCell>
                                  <TableCell className="hidden sm:table-cell text-xs">{s.components?.bureau_score?.score || '-'}</TableCell>
                                  <TableCell className="hidden sm:table-cell text-xs">{s.components?.imss_verification?.score || '-'}</TableCell>
                                  <TableCell className="hidden sm:table-cell text-xs">{s.components?.payment_history?.score || '-'}</TableCell>
                                  <TableCell className="hidden sm:table-cell text-xs">{s.components?.debt_income_ratio?.score || '-'}</TableCell>
                                  <TableCell className="text-xs">{s.calculated_at ? formatDate(s.calculated_at) : '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Component Weights */}
                  <Card>
                    <CardHeader><CardTitle>Pesos del Motor de Riesgo</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { name: 'Score Buro', weight: 35, color: '#0033A0', icon: '35%' },
                          { name: 'Historial Pagos Ed.Fi', weight: 25, color: '#E91E8C', icon: '25%' },
                          { name: 'Ratio Deuda/Ingreso', weight: 25, color: '#F59E0B', icon: '25%' },
                          { name: 'Verificacion IMSS', weight: 15, color: '#10B981', icon: '15%' },
                        ].map(w => (
                          <div key={w.name} className="text-center p-4 rounded-xl" style={{ backgroundColor: w.color + '10' }}>
                            <p className="text-3xl font-bold" style={{ color: w.color }}>{w.icon}</p>
                            <p className="text-sm text-slate-700 mt-1">{w.name}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
              {!riskOverview && <p className="text-center text-slate-500 py-12">Cargando datos de riesgo...</p>}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
